import React, { useEffect, useState } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import { DashboardLayout } from '../components/dashboard';
import { userService } from '../services/userService';
import { ownershipService, OwnershipAssignment } from '../services/ownershipService';
import { ConfirmModal } from '../components/common/ConfirmModal';
import { OrganizationTreeSelect } from '../components/organizations/OrganizationTreeSelect';
import { MessageModal } from '../components/common/MessageModal';
import { useUrlState } from '../hooks/useUrlState';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  Field,
  IconButton,
  Page,
  PageHeader,
  Pill,
  SectionHeader,
  Select,
  Skeleton,
  ENTITY_TONE,
  entityKindOf,
} from '../components/ui';

type Owner = { id: string; email: string; firstName?: string; lastName?: string };

const ownerName = (owner?: Owner) => (owner ? `${owner.firstName || ''} ${owner.lastName || ''}`.trim() : '');

const initials = (text: string) =>
  text
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

/** Scope pills take the hue of the entity kind they grant: organizations teal, people sky */
const SCOPE_TONE: Record<string, 'teal' | 'blue' | 'outline'> = {
  organization: 'teal',
  stakeholder: 'blue',
};

export const AccessControlPage: React.FC = () => {
  const [topOwners, setTopOwners] = useState<Owner[]>([]);
  // Who and what is being assigned lives in the URL, so a half-filled form
  // survives a detour to another page and can be handed to someone as a link
  const [selectedOwner, setSelectedOwner] = useUrlState('owner', '');
  const [orgParam, setOrgParam] = useUrlState('org', '');
  const selectedOrgId = orgParam || null;
  const [assignments, setAssignments] = useState<OwnershipAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<{ open: boolean; title: string; message: React.ReactNode }>({ open: false, title: '', message: '' });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const usersResp = await userService.getUsers({ role: 'top_owner', isActive: true, limit: 100 });
        setTopOwners(usersResp.users);
        const a = await ownershipService.listAssignments();
        setAssignments(a);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // A link can name an owner who is no longer a top owner: drop them rather
  // than leave the picker looking empty but armed
  useEffect(() => {
    if (loading || !selectedOwner) return;
    if (!topOwners.some((owner) => owner.id === selectedOwner)) setSelectedOwner('');
  }, [loading, topOwners, selectedOwner, setSelectedOwner]);

  const handleAssign = async () => {
    if (!selectedOwner || !selectedOrgId) return;
    try {
      setSubmitting(true);
      await ownershipService.assign({ ownerId: selectedOwner, scope: 'organization', scopeId: selectedOrgId });
      const a = await ownershipService.listAssignments();
      setAssignments(a);
      setOrgParam('');
      setSelectedOwner('');
    } catch (err: any) {
      setNotice({ open: true, title: 'Assignment Failed', message: err.response?.data?.message || 'Assignment failed' });
    } finally {
      setSubmitting(false);
    }
  };

  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const handleRemove = async (id: string) => { setConfirmId(id); };

  const ownersById = new Map(topOwners.map((owner) => [owner.id, owner]));

  return (
    <DashboardLayout>
      <Page>
        <PageHeader
          title="Access"
          meta={!loading && !error ? `${assignments.length} ${assignments.length === 1 ? 'assignment' : 'assignments'}` : undefined}
        />

        {error && (
          <p role="alert" className="mb-5 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
            {error}
          </p>
        )}

        <SectionHeader title="Assign owner" />
        <Card padded>
          {loading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end" aria-busy="true">
              <Skeleton className="h-9 w-full rounded-xl" />
              <Skeleton className="h-9 w-full rounded-xl" />
              <Skeleton className="h-9 w-20 rounded-full" />
            </div>
          ) : error ? (
            <p className="text-[13px] text-neutral-500">Unavailable</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
              <Field label="Top owner" htmlFor="access-owner">
                <Select id="access-owner" value={selectedOwner} onChange={(e) => setSelectedOwner(e.target.value)}>
                  <option value="">{topOwners.length ? 'Select owner' : 'No top owners'}</option>
                  {topOwners.map((u) => (
                    <option key={u.id} value={u.id}>
                      {ownerName(u) ? `${ownerName(u)} (${u.email})` : u.email}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Organization">
                <OrganizationTreeSelect
                  selectedId={selectedOrgId || undefined}
                  onSelect={(id) => setOrgParam(id || '')}
                  placeholder="Select organization"
                />
              </Field>
              <Button
                variant="primary"
                disabled={!selectedOwner || !selectedOrgId}
                loading={submitting}
                onClick={handleAssign}
                className="w-full sm:w-auto"
              >
                Assign
              </Button>
            </div>
          )}
        </Card>

        <SectionHeader title="Assignments" className="mt-8" />
        <Card>
          {loading ? (
            <div className="divide-y divide-neutral-200/70" aria-busy="true" aria-label="Loading assignments">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <Skeleton className="h-4 w-44" />
                </div>
              ))}
            </div>
          ) : assignments.length === 0 ? (
            <EmptyState icon={ShieldCheck} title="No assignments" />
          ) : (
            <ul className="divide-y divide-neutral-200/70">
              {assignments.map((a) => {
                const owner = ownersById.get(a.ownerId);
                const name = ownerName(owner) || owner?.email || '';
                const target = a.scopeId || a.scopeValue || '—';
                const scopeKind = entityKindOf(a.scope);
                return (
                  <li key={a.id} className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-neutral-50">
                    <Avatar kind="neutral" size="md" initials={initials(name || a.ownerId)} />
                    <div className="min-w-0 flex-1">
                      {name ? (
                        <p className="truncate text-[14px] text-ink">{name}</p>
                      ) : (
                        <p className="truncate font-ui-mono text-[12.5px] text-ink" title={a.ownerId}>
                          {a.ownerId}
                        </p>
                      )}
                      <p className="truncate text-[12px] text-neutral-400">
                        {owner && ownerName(owner) && <span className="hidden sm:inline">{owner.email}</span>}
                        <span className="sm:hidden">
                          <span className={`capitalize ${scopeKind ? ENTITY_TONE[scopeKind].text : ''}`}>{a.scope}</span> ·{' '}
                          <span className="font-ui-mono" title={target}>
                            {target}
                          </span>
                        </span>
                      </p>
                    </div>
                    <Pill tone={SCOPE_TONE[a.scope] || 'outline'} className="hidden capitalize sm:inline-flex">
                      {a.scope}
                    </Pill>
                    <span
                      className="hidden w-[180px] truncate text-right font-ui-mono text-[12px] text-neutral-400 sm:block lg:w-[300px]"
                      title={target}
                    >
                      {target}
                    </span>
                    <IconButton
                      label="Remove"
                      size="sm"
                      onClick={() => handleRemove(a.id)}
                      className="hover:bg-primary-50 hover:text-primary-700"
                    >
                      <X className="h-4 w-4" strokeWidth={1.75} />
                    </IconButton>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </Page>
      <ConfirmModal
        open={!!confirmId}
        title="Remove ownership"
        message={<span>Remove this ownership assignment?</span>}
        confirmText="Remove"
        loading={removing}
        onCancel={() => setConfirmId(null)}
        onConfirm={async () => {
          if (!confirmId) return;
          try { setRemoving(true); await ownershipService.remove(confirmId); setAssignments(await ownershipService.listAssignments()); } finally { setRemoving(false); setConfirmId(null); }
        }}
      />
      <MessageModal open={notice.open} title={notice.title} message={notice.message} onClose={() => setNotice(n => ({ ...n, open: false }))} />
    </DashboardLayout>
  );
};
